import type { WorkflowDocument, WorkflowNode } from '../../store/api/workflowsApi';

export interface LaidOutNode {
  id: string;
  rank: number;
  row: number;
}

/**
 * Columns from the forward graph, ignoring return edges. A node's rank is its longest
 * path from the trigger, so a step always sits right of everything leading to it. Any
 * valid document can be drawn, including a step nothing leads to.
 */
export function layoutGraph(document: WorkflowDocument): LaidOutNode[] {
  const ids = document.nodes.map((node) => node.id);
  const known = new Set(ids);
  const forward = document.edges.filter(
    (edge) => !edge.loop && known.has(edge.from) && known.has(edge.to),
  );
  const rank = new Map<string, number>(ids.map((id) => [id, 0]));
  // Bounded by the node count, so a cycle with no loop mark cannot spin forever.
  for (let pass = 0; pass < ids.length; pass += 1) {
    let changed = false;
    for (const edge of forward) {
      const next = (rank.get(edge.from) ?? 0) + 1;
      if (next > (rank.get(edge.to) ?? 0) && next < ids.length) {
        rank.set(edge.to, next);
        changed = true;
      }
    }
    if (!changed) break;
  }
  const rows = new Map<number, number>();
  return ids.map((id) => {
    const column = rank.get(id) ?? 0;
    const row = rows.get(column) ?? 0;
    rows.set(column, row + 1);
    return { id, rank: column, row };
  });
}

/** How a workflow starts, in words. The wording comes from the document alone. */
export function triggerWords(document: WorkflowDocument | null | undefined): string {
  const trigger = document?.trigger;
  if (trigger?.kind === 'schedule') {
    const schedule = trigger.schedule;
    return `Starts ${schedule?.display || schedule?.cron} (${schedule?.timezone}), or from the API.`;
  }
  if (trigger?.kind === 'event') {
    return `Starts when ${trigger.event?.name} arrives, or from the API.`;
  }
  return 'Starts from Run or the API.';
}

/** The fields that differ between two documents, for the review dialog. */
export function documentChanges(
  before: WorkflowDocument | null | undefined,
  after: WorkflowDocument | null | undefined,
): string[] {
  if (!after) return [];
  if (!before) return ['This becomes the first live version.'];
  const changes: string[] = [];
  const beforeNodes = new Map(before.nodes.map((node) => [node.id, node]));
  const afterNodes = new Map(after.nodes.map((node) => [node.id, node]));
  for (const [id, node] of afterNodes) {
    const old = beforeNodes.get(id);
    const title = node.title || id;
    if (!old) changes.push(`Added step: ${title}`);
    else if (JSON.stringify(old) !== JSON.stringify(node)) changes.push(`Changed step: ${title}`);
  }
  for (const [id, node] of beforeNodes) {
    if (!afterNodes.has(id)) changes.push(`Removed step: ${node.title || id}`);
  }
  if (JSON.stringify(before.edges) !== JSON.stringify(after.edges)) {
    changes.push('Changed how the steps connect');
  }
  if (JSON.stringify(before.trigger) !== JSON.stringify(after.trigger)) {
    changes.push(`Changed how it starts: ${triggerWords(after)}`);
  }
  if (before.description !== after.description) changes.push('Changed the description');
  if (JSON.stringify(before.input) !== JSON.stringify(after.input)) {
    changes.push('Changed what a run needs');
  }
  return changes.length ? changes : ['No changes to the steps.'];
}

/** One sentence for the run bar: the focal point of the Runs tab. */
export function runVerdict(run: {
  status: string;
  outcome?: string | null;
  error?: string | null;
  steps?: { title: string | null; id: string; status: string }[];
}): string {
  const stepNamed = (status: string) => {
    const step = run.steps?.find((item) => item.status === status);
    return step ? step.title || step.id : null;
  };
  if (run.status === 'failed') {
    const step = stepNamed('failed') || stepNamed('interrupted');
    const reason = run.error || 'The run could not complete.';
    return step ? `Stopped on ${step}: ${reason}` : reason;
  }
  if (run.status === 'waiting') return `Waiting on ${stepNamed('waiting') || 'a step'}.`;
  if (run.status === 'running') return `Running ${stepNamed('running') || ''}`.trim() + '.';
  if (run.status === 'queued') return 'Starting.';
  if (run.status === 'cancelled') return 'Cancelled.';
  return run.outcome ? `Finished: ${run.outcome}.` : 'Finished.';
}

/** The step a run's panel opens on: failed, then waiting, then running, then the last. */
export function stepThatMatters<T extends { id: string; status: string; taken?: boolean }>(
  steps: T[],
): T | undefined {
  for (const status of ['failed', 'interrupted', 'waiting', 'running']) {
    const found = steps.find((step) => step.status === status);
    if (found) return found;
  }
  return [...steps].reverse().find((step) => step.taken);
}

/** What Agent Builder starts from when a step needs an agent that does not exist yet. */
export function agentBrief(node: WorkflowNode): string {
  return [
    node.title || node.id,
    node.purpose,
    node.input?.instruction && `It is told to: ${node.input.instruction}`,
    node.input?.description && `It receives: ${node.input.description}`,
    node.output?.description && `It must return: ${node.output.description}`,
    node.output?.format && `Output format: ${node.output.format}`,
  ]
    .filter(Boolean)
    .join('\n');
}
